/* iter-payments-master — Global payments-status hook.
 *
 * Wraps `/api/payments/config` behind a lightweight cache + polling
 * layer so every "Buy / Subscribe / Checkout" button in the app can
 * read the master switch without every component fetching separately.
 *
 * Shape returned by the hook:
 *   {
 *     ready: boolean,           // true once the first fetch resolves
 *     enabled: boolean,         // false when admin flipped payments OFF
 *     reason: string | null,    // admin-set downtime message (may be null)
 *   }
 *
 * Additionally sets `document.body.dataset.paymentsDisabled = "1"`
 * when payments are OFF so global CSS can grey out anything with the
 * `[data-pay-button]` attribute.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const POLL_MS = 30_000;   // 30 s — feels near-instant without hammering
const _listeners = new Set();
const _state = { ready: false, enabled: true, reason: null };
let _timer = null;
let _inflight = false;

function _publishBodyAttr() {
  if (typeof document === 'undefined') return;
  if (_state.ready && !_state.enabled) {
    document.body.dataset.paymentsDisabled = '1';
  } else {
    delete document.body.dataset.paymentsDisabled;
  }
}

/** Log a shopper interaction with a payment CTA while payments are OFF. */
export function logDowntimeClick(surface, control, extra = {}) {
  try {
    axios.post(`${API}/payments/downtime-click`, {
      surface, control,
      tier_or_pack: extra.tierOrPack || null,
      url: (typeof window !== 'undefined' ? window.location.href : null),
      user_id: extra.userId || null,
    }, { timeout: 5000 }).catch(() => { /* silent */ });
  } catch { /* silent */ }
}

function _notify() {
  _publishBodyAttr();
  for (const cb of _listeners) {
    try { cb({ ..._state }); } catch { /* isolate crashes per listener */ }
  }
}

async function _pull() {
  if (_inflight) return;
  _inflight = true;
  try {
    const r = await axios.get(`${API}/payments/config`, { timeout: 8000 });
    const d = r?.data || {};
    _state.enabled = d.payments_enabled !== false;
    _state.reason  = d.payments_downtime_reason || null;
    _state.ready   = true;
    _notify();
  } catch {
    // Silent — leave last known good state in place, keep polling.
  } finally {
    _inflight = false;
  }
}

function _ensurePolling() {
  if (_timer) return;
  _pull();
  _timer = setInterval(_pull, POLL_MS);
}

/**
 * React hook — subscribes to the shared status singleton.
 * Multiple call sites share the same fetch loop.
 */
export function usePaymentsStatus() {
  const [snapshot, setSnapshot] = useState(_state);
  useEffect(() => {
    const cb = (s) => setSnapshot(s);
    _listeners.add(cb);
    _ensurePolling();
    return () => { _listeners.delete(cb); };
  }, []);
  return snapshot;
}

/** Imperative refetch — call after an admin flips the switch. */
export function refreshPaymentsStatus() { _pull(); }
